import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { generateVerificationId } from '../utils/verificationUtils';

/**
 * Create a new case when a search is completed with results
 * @param {Object} searchData - The search data
 * @param {string} searchData.searchTerm - The search term
 * @param {string} searchData.searchId - ID of the search in sanction-searches collection
 * @param {string} searchData.orgId - ID of the organization
 * @param {string} searchData.userId - ID of the user who performed the search
 * @param {string} searchData.verificationId - Verification ID for the search
 * @param {Object} searchData.stats - Search statistics
 * @param {number} searchData.totalMatches - Total number of matches found
 * @param {string} searchData.source - Source of the search (individual, mass-upload, api)
 * @returns {Promise<string>} - ID of the created case
 */
export const createCase = async (searchData) => {
  try {
    console.log("Creating new case for search:", {
      searchTerm: searchData.searchTerm,
      searchId: searchData.searchId,
      verificationId: searchData.verificationId
    });
    
    // Don't create a case if there's no org ID or user ID
    if (!searchData.orgId || !searchData.userId) {
      console.log('Cannot create case: No organization or user ID provided');
      return null;
    }
    
    // Don't create a case if there are no results/matches
    const totalMatches = searchData.totalMatches || searchData.stats?.totalMatches || 0;
    if (totalMatches === 0) {
      console.log('No matches found, skipping case creation');
      return null;
    }
    
    // Generate a verification ID if not provided
    let verificationId = searchData.verificationId;
    if (!verificationId) {
      const timestamp = Date.now();
      verificationId = await generateVerificationId(searchData.searchTerm, timestamp);
      console.log('Generated new verification ID:', verificationId);
    }
    
    // Calculate confidence level counts
    const confidenceCounts = {
      veryHigh: searchData.stats?.veryHighConfidence || 0,
      high: searchData.stats?.highConfidence || 0,
      medium: searchData.stats?.mediumConfidence || 0,
      low: searchData.stats?.lowConfidence || 0
    };
    
    // Create a new document in the cases collection
    const casesRef = collection(db, 'cases');
    
    // Prepare case data
    const caseData = {
      // Basic information
      searchTerm: searchData.searchTerm,
      orgId: searchData.orgId,
      userId: searchData.userId,
      verificationId: verificationId,
      searchId: searchData.searchId,
      
      // Status information
      status: 'pending', // pending, partially-reviewed, completed
      
      // Match information
      totalMatches: totalMatches,
      matchCounts: confidenceCounts,
      reviewCounts: {
        pending: totalMatches,
        confirmed: 0,
        dismissed: 0
      },
      
      // Source information
      source: searchData.source || 'individual',
      batchJobId: searchData.batchJobId || null,
      
      // Timestamps
      created: serverTimestamp(), // When the case was created
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Additional metadata
      entityType: searchData.entityType || 'person'
    };
    
    // Add the document to Firestore
    const docRef = await addDoc(casesRef, caseData);
    console.log('Case created with ID:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating case:', error);
    return null;
  }
};

/**
 * Get all cases for a specific organization
 * @param {string} orgId - The organization ID
 * @returns {Promise<Array>} - Array of case objects
 */
export const getUserCases = async (orgId) => {
  try {
    console.log("Fetching cases for organization:", orgId);
    
    // Create a reference to the cases collection
    const casesRef = collection(db, 'cases');
    
    // Create a query for the organization's cases, ordered by creation time (newest first)
    const casesQuery = query(
      casesRef,
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    
    // Execute the query
    const querySnapshot = await getDocs(casesQuery);
    console.log(`Found ${querySnapshot.size} cases for user`);
    
    // Process the results
    const cases = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      // Convert Firestore timestamps to JavaScript Date objects
      const timestamp = data.createdAt?.toDate?.() || new Date();
      
      return {
        id: doc.id,
        ...data,
        timestamp,
        formattedDate: timestamp.toLocaleDateString()
      };
    });
    
    return cases;
  } catch (error) {
    console.error('Error fetching user cases:', error);
    throw error;
  }
};

/**
 * Get a case by ID
 * @param {string} caseId - The case ID
 * @param {string} orgId - The organization ID (for authorization)
 * @returns {Promise<Object|null>} - The case data or null if not found
 */
export const getCaseById = async (caseId, orgId) => {
  try {
    console.log('Fetching case by ID:', caseId);
    
    // Get the document from Firestore
    const docRef = doc(db, 'cases', caseId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log('Case not found');
      return null;
    }
    
    const data = docSnap.data();
    
    // Ensure only users from the same organization can access it
    if (data.orgId !== orgId) {
      console.error('Unauthorized access attempt to case');
      return null;
    }
    
    // Convert Firestore timestamps to JavaScript Date objects
    const timestamp = data.createdAt?.toDate?.() || new Date();
    
    return {
      id: caseId,
      ...data,
      timestamp,
      formattedDate: timestamp.toLocaleDateString()
    };
  } catch (error) {
    console.error('Error fetching case by ID:', error);
    return null;
  }
};

/**
 * Update review status for a specific result in a case
 * @param {string} caseId - The case ID
 * @param {string} resultId - The result ID
 * @param {Object} reviewData - The review data
 * @param {string} reviewData.review_status - Review status (pending, confirmed, dismissed)
 * @param {string} reviewData.review_notes - Optional notes about the review
 * @param {string} orgId - The organization ID (for authorization)
 * @param {string} userId - The user ID (for audit trail)
 * @returns {Promise<boolean>} - Whether the update was successful
 */
export const updateResultReviewStatus = async (caseId, resultId, reviewData, orgId, userId) => {
  try {
    console.log('Updating result review status:', { caseId, resultId, reviewData });
    
    // Get the current case data
    const caseData = await getCaseById(caseId, orgId);
    
    if (!caseData) {
      console.error('Case not found or unauthorized');
      return false;
    }
    
    // Get current review statuses or initialize empty object
    const reviewStatuses = caseData.reviewStatuses || {};
    
    // Update the specific result's review status
    reviewStatuses[resultId] = {
      review_status: reviewData.review_status,
      review_notes: reviewData.review_notes || '',
      review_timestamp: new Date().toISOString(),
      reviewer_userID: userId
    };
    
    // Calculate review counts
    const reviewCounts = {
      pending: 0,
      confirmed: 0,
      dismissed: 0
    };
    
    // Count total results that should have reviews
    const totalMatches = caseData.totalMatches || 0;
    let reviewedCount = 0;
    
    Object.values(reviewStatuses).forEach(review => {
      reviewedCount++;
      switch (review.review_status) {
        case 'confirmed':
          reviewCounts.confirmed++;
          break;
        case 'dismissed':
          reviewCounts.dismissed++;
          break;
        default:
          reviewCounts.pending++;
          break;
      }
    });
    
    // Add pending count for unreviewed results
    reviewCounts.pending += Math.max(0, totalMatches - reviewedCount);
    
    // Determine the new case status
    let status = 'pending';
    if (reviewCounts.pending === 0 && totalMatches > 0) {
      status = 'completed';
    } else if (reviewCounts.confirmed > 0 || reviewCounts.dismissed > 0) {
      status = 'partially-reviewed';
    }
    
    // Update the case document
    const caseRef = doc(db, 'cases', caseId);
    await updateDoc(caseRef, {
      reviewStatuses,
      status,
      reviewCounts,
      updatedAt: serverTimestamp()
    });
    
    console.log('Result review status updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating result review status:', error);
    return false;
  }
};

/**
 * Get review status for all results in a case
 * @param {string} caseId - The case ID
 * @param {string} orgId - The organization ID (for authorization)
 * @returns {Promise<Object>} - Object with resultId as key and review data as value
 */
export const getReviewStatusesForCase = async (caseId, orgId) => {
  try {
    const caseData = await getCaseById(caseId, orgId);
    
    if (!caseData) {
      console.error('Case not found or unauthorized');
      return {};
    }
    
    return caseData.reviewStatuses || {};
  } catch (error) {
    console.error('Error getting review statuses for case:', error);
    return {};
  }
};

/**
 * Get a case by verification ID
 * @param {string} verificationId - The verification ID
 * @param {string} orgId - The organization ID (for authorization)
 * @returns {Promise<Object|null>} - The case data or null if not found
 */
export const getCaseByVerificationId = async (verificationId, orgId) => {
  try {
    console.log('Fetching case by verification ID:', verificationId);
    
    // Create a query for the case with the given verification ID
    const casesRef = collection(db, 'cases');
    const caseQuery = query(
      casesRef,
      where('verificationId', '==', verificationId),
      where('orgId', '==', orgId)
    );
    
    // Execute the query
    const querySnapshot = await getDocs(caseQuery);
    
    if (querySnapshot.empty) {
      console.log('Case not found with verification ID:', verificationId);
      return null;
    }
    
    // Get the first matching document
    const docSnap = querySnapshot.docs[0];
    const data = docSnap.data();
    
    // Convert Firestore timestamps to JavaScript Date objects
    const timestamp = data.createdAt?.toDate?.() || new Date();
    
    return {
      id: docSnap.id,
      ...data,
      timestamp,
      formattedDate: timestamp.toLocaleDateString()
    };
  } catch (error) {
    console.error('Error fetching case by verification ID:', error);
    return null;
  }
};

/**
 * Get case statistics for an organization
 * @param {string} orgId - The organization ID
 * @returns {Promise<Object>} - Statistics about the organization's cases
 */
export const getCaseStats = async (orgId) => {
  try {
    console.log('Fetching case statistics for organization:', orgId);
    
    // Get all cases for the organization
    const cases = await getUserCases(orgId);
    
    // Calculate statistics
    const stats = {
      total: cases.length,
      pending: cases.filter(c => c.status === 'pending').length,
      inProgress: cases.filter(c => c.status === 'partially-reviewed').length,
      completed: cases.filter(c => c.status === 'completed').length
    };
    
    return stats;
  } catch (error) {
    console.error('Error fetching case statistics:', error);
    return {
      total: 0,
      pending: 0,
      inProgress: 0,
      completed: 0
    };
  }
};

/**
 * Get a case by search ID
 * @param {string} searchId - The search ID
 * @param {string} orgId - The organization ID (for authorization)
 * @returns {Promise<Object|null>} - The case data or null if not found
 */
export const getCaseBySearchId = async (searchId, orgId) => {
  try {
    console.log('Fetching case by search ID:', searchId);
    
    // Create a query for the case with the given search ID
    const casesRef = collection(db, 'cases');
    const caseQuery = query(
      casesRef,
      where('searchId', '==', searchId),
      where('orgId', '==', orgId)
    );
    
    // Execute the query
    const querySnapshot = await getDocs(caseQuery);
    
    if (querySnapshot.empty) {
      console.log('Case not found with search ID:', searchId);
      return null;
    }
    
    // Get the first matching document
    const docSnap = querySnapshot.docs[0];
    const data = docSnap.data();
    
    // Convert Firestore timestamps to JavaScript Date objects
    const timestamp = data.createdAt?.toDate?.() || new Date();
    
    return {
      id: docSnap.id,
      ...data,
      timestamp,
      formattedDate: timestamp.toLocaleDateString()
    };
  } catch (error) {
    console.error('Error fetching case by search ID:', error);
    return null;
  }
}; 